/**
 * stripeConnectPolicy.ts — Country → currency mapping for the frontend.
 *
 * Frontend mirror of the country half of
 * supabase/functions/_shared/stripeConnectPolicy.ts.
 *
 * Why a mirror and not an import: `supabase functions deploy` bundles from
 * `supabase/functions/`, so a cross-import from `src/` breaks the deploy
 * bundle, and `_shared/` has to stay free of any Vite dependency. The two
 * copies are kept honest by src/__tests__/unit/edge/stripeConnectPolicyMirror.test.ts,
 * which fails if the maps or `resolveCountry`'s behaviour diverge — the sync
 * mechanism is that test, not this comment.
 *
 * The payout-schedule half is deliberately NOT mirrored: the frontend does not
 * decide when Stripe pays a bank account. `resolvePayoutSchedule` lives only in
 * `_shared/`, where the Stripe Connect account is created.
 *
 * "profiles.country" is stored as a lowercase human-readable slug
 * (e.g. "brazil", "united-states").
 */

export interface CountryConfig {
  /** Stripe-compatible ISO 3166-1 alpha-2 country code. */
  iso: string;
  /** Default currency for instructor payouts in this country. */
  currency: string;
}

/**
 * Maps the lowercase slug stored in `profiles.country` (or an ISO code) to its
 * Stripe ISO country code and default payout currency.
 *
 * The platform currently operates in USA and Brazil only. Expanding this map is
 * SOS-352, not this change.
 *
 * Accepts both the human-readable slug ("brazil") and the ISO 3166-1 alpha-2
 * code ("BR"). Case-insensitive.
 */
export const COUNTRY_MAP: Record<string, CountryConfig> = {
  // Brazil
  "brazil":         { iso: "BR", currency: "BRL" },
  "br":             { iso: "BR", currency: "BRL" },

  // United States
  "united-states":  { iso: "US", currency: "USD" },
  "usa":            { iso: "US", currency: "USD" },
  "us":             { iso: "US", currency: "USD" },
};

export const DEFAULT_COUNTRY: CountryConfig = { iso: "US", currency: "USD" };

/**
 * Resolves a profile country slug or ISO code to a Stripe country config.
 *
 * Behaviour:
 *   - `null` / `undefined` / empty   → DEFAULT_COUNTRY (US) — onboarding pré-perfil
 *   - "brazil" / "br" / "BR"         → { iso: "BR", currency: "BRL" }
 *   - "usa" / "us" / "united-states" → { iso: "US", currency: "USD" }
 *   - anything else                  → throws (avoids silent US fallback)
 *
 * Throwing is inherited from the production copy on purpose: the two sides must
 * behave identically, and a silent US fallback here would disagree with the
 * account Stripe actually creates. Display surfaces that must never break on an
 * unsupported country catch this — see `usePayoutCurrency`.
 *
 * @example
 *   resolveCountry("brazil")  // { iso: "BR", currency: "BRL" }
 *   resolveCountry("BR")      // { iso: "BR", currency: "BRL" }
 *   resolveCountry(null)      // { iso: "US", currency: "USD" }
 *   resolveCountry("germany") // throws — not supported
 */
export function resolveCountry(slug: string | null | undefined): CountryConfig {
  if (!slug) return DEFAULT_COUNTRY;
  const config = COUNTRY_MAP[slug.toLowerCase()];
  if (!config) {
    throw new Error(
      `Country "${slug}" is not supported. The platform currently operates ` +
      `in Brazil (BR) and the United States (US) only.`
    );
  }
  return config;
}
